import React, { useState, useCallback } from 'react';

export interface SplitPaneProps {
  left: React.ReactNode;
  right: React.ReactNode;
  defaultSplit?: number; // percentage (0-100)
  minSize?: number; // minimum pixels
  className?: string;
}

export const SplitPane: React.FC<SplitPaneProps> = ({
  left,
  right,
  defaultSplit = 50,
  minSize = 200,
  className = ''
}) => {
  const [splitPosition, setSplitPosition] = useState(defaultSplit);
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    
    const handleMouseMove = (e: MouseEvent) => {
      const container = document.getElementById('split-pane-container');
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const newPosition = ((e.clientX - rect.left) / rect.width) * 100;
      
      // Enforce minimum sizes
      const minLeftPercent = (minSize / rect.width) * 100;
      const minRightPercent = 100 - ((minSize / rect.width) * 100);
      
      setSplitPosition(
        Math.max(minLeftPercent, Math.min(minRightPercent, newPosition))
      );
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [minSize]);

  return (
    <div 
      id="split-pane-container"
      className={`h-full flex ${className}`}
      style={{ userSelect: isDragging ? 'none' : 'auto' }}
    >
      <div 
        className="flex-none border-r border-gray-200"
        style={{ width: `${splitPosition}%` }}
      >
        {left}
      </div>
      
      <div 
        className="w-1 bg-gray-200 hover:bg-gray-300 cursor-col-resize flex items-center justify-center"
        onMouseDown={handleMouseDown}
      >
        <div className="w-0.5 h-8 bg-gray-400 rounded-full" />
      </div>
      
      <div 
        className="flex-1"
        style={{ width: `${100 - splitPosition}%` }}
      >
        {right}
      </div>
    </div>
  );
};